import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../contexts/AuthContext'

import { API_BASE_URL } from '../config/api'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  const [stats, setStats] = useState({})
  const [users, setUsers] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/home')
      return
    }
    fetchAdminData()
  }, [user, navigate])

  const fetchAdminData = async () => {
    try {
      setLoading(true)
      const [usersRes, eventsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/api/admin/events`, { withCredentials: true })
      ])

      setUsers(usersRes.data.users || [])
      setEvents(eventsRes.data || [])

      // Calculate stats
      setStats({
        totalUsers: usersRes.data.totalUsers || 0,
        totalEvents: eventsRes.data.length || 0,
        activeUsers: usersRes.data.users?.filter(u =>
          u.lastLoginAt && new Date(u.lastLoginAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ).length || 0
      })
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching admin data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, { withCredentials: true })
      await fetchAdminData()
      alert('User deleted successfully')
    } catch (error) {
      alert('Failed to delete user: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      )
      await fetchAdminData()
      alert('User role updated successfully')
    } catch (error) {
      alert('Failed to update role: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleClearInactiveUsers = async () => {
    if (!window.confirm('Delete all users who have not logged in for 90+ days?')) {
      return
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/clear-inactive`,
        { days: 90 },
        { withCredentials: true }
      )
      await fetchAdminData()
      alert(`Successfully deleted ${response.data.deletedCount} inactive users`)
    } catch (error) {
      alert('Failed to clear inactive users: ' + (error.response?.data?.message || error.message))
    }
  }

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center">
        <div className="text-white text-xl">Loading admin dashboard...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-300">Manage users, events, and system settings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="text-gray-300 text-sm mb-2">Total Users</div>
            <div className="text-white text-3xl font-bold">{stats.totalUsers}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="text-gray-300 text-sm mb-2">Total Events</div>
            <div className="text-white text-3xl font-bold">{stats.totalEvents}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="text-gray-300 text-sm mb-2">Active Users (7 days)</div>
            <div className="text-white text-3xl font-bold">{stats.activeUsers}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/20">
          {['overview', 'users', 'events', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-4 capitalize transition-colors ${
                activeTab === tab
                  ? 'text-white border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">System Overview</h2>
              <div className="space-y-4 text-gray-300">
                <p>Welcome to the Main Stage admin dashboard.</p>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-white/5 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">Database Status</div>
                    <div className="text-white font-semibold">Connected</div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-lg">
                    <div className="text-sm text-gray-400">Server Status</div>
                    <div className="text-white font-semibold">Online</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">User Management</h2>
                <button
                  onClick={handleClearInactiveUsers}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Clear Inactive Users (90+ days)
                </button>
              </div>

              {/* Search */}
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white mb-6"
              />

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="border-b border-white/20">
                    <tr>
                      <th className="pb-3 text-gray-300">Username</th>
                      <th className="pb-3 text-gray-300">Email</th>
                      <th className="pb-3 text-gray-300">Role</th>
                      <th className="pb-3 text-gray-300">Last Login</th>
                      <th className="pb-3 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u._id} className="border-b border-white/10">
                        <td className="py-3 text-white">{u.username}</td>
                        <td className="py-3 text-gray-300">{u.email}</td>
                        <td className="py-3">
                          <select
                            value={u.role || 'user'}
                            onChange={(e) => handleUpdateUserRole(u._id, e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm"
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="py-3 text-gray-300">
                          {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => handleDeleteUser(u._id)}
                            className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1 rounded text-sm transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'events' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Event Management</h2>
              <p className="text-gray-300 mb-4">Total Events: {events.length}</p>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {events.slice(0, 20).map(event => (
                  <div key={event._id} className="bg-white/5 p-4 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-white font-semibold">{event.name}</div>
                        <div className="text-gray-400 text-sm">
                          {event.venueName} • {new Date(event.startDate).toLocaleDateString()}
                        </div>
                        <div className="text-gray-500 text-sm">
                          Created by: {event.createdBy?.username || 'Unknown'}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        Privacy: {event.privacy || 'public'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">System Settings</h2>
              <div className="space-y-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-white font-semibold mb-2">Environment</div>
                  <div className="text-gray-300">{process.env.NODE_ENV || 'development'}</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <div className="text-white font-semibold mb-2">API Keys Status</div>
                  <div className="text-gray-300">
                    <div>Ticketmaster API: {process.env.REACT_APP_TICKETMASTER_API_KEY ? '✓ Configured' : '✗ Missing'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
